import React from "react";
import RevisionChildHarness from "./RevisionChildHarness";
import CopyHarness from "./CopyHarness";
import Stage3Harness from "./Stage3Harness";
import { CssBaseline, ThemeProvider, Container } from "@mui/material";
import theme from "../../theme/theme";
import SocialsPage from "./SocialsPage";
import type { HealthRow } from "./BehindTheScenesDialog";
import { REALISTIC_QUEUE } from "./__fixtures__/reviewCards";
import { ENGAGE_QUEUE } from "./__fixtures__/engageLeads";
import { FIXTURE_SCHEDULED, FIXTURE_POSTED } from "./__fixtures__/scheduleFixtures";
import { counted, unknown } from "../../lib/socialsCounts";

/**
 * Ch94 · LOCAL HARNESS for the Socials tab. Dev-only, reached at
 * `?socials-harness`; nothing links to it and it ships no data.
 *
 * 🔴 WHY A HARNESS RATHER THAN THE REAL APP. Local dev CANNOT point at live
 * data, by design and correctly: `runtimeEnvironment.ts` throws at import time
 * on the live Supabase refs and the production API/app/storage hosts outside a
 * production build, and `setupProxy.js` throws independently for a prod API
 * target. That guard is not weakened to see a screen — this renders the real
 * components against the fixture queue instead.
 *
 * What it therefore CANNOT prove: real-data behaviour, real timings, and the
 * true distribution of absent fields. Those are live-browser observations.
 * What it DOES prove: layout at phone and desktop width, every binding rule,
 * and how the surface degrades when fields are missing — which is most of what
 * this packet is about.
 */

const HEALTH: HealthRow[] = [
  { label: "Engine last ran", value: "2 hours ago", tone: "fine" },
  { label: "Review sync", value: "Up to date", tone: "fine" },
  {
    label: "Decisions that failed to apply",
    value: counted(2),
    tone: "act",
    action: "These need an admin to re-send them.",
    items: ["Emmi Midtown — tour", "The Juniper — tour"],
  },
  { label: "Instagram token expires", value: "31 October", tone: "watch" },
  // Deliberately unknown rather than a confident number: nothing measures it yet.
  { label: "Wording variety", value: unknown(), tone: "fine" },
];

// Synthetic long queue for checking the collapsed Reddit list and Instagram access.
const longEngageQueue = Array.from({ length: 12 }, (_, index) => ({
  ...ENGAGE_QUEUE[0],
  id: `preview-${index}`,
  source_url: `https://www.reddit.com/r/test/comments/preview${index}/`,
  raw_payload: { title: `Synthetic apartment search ${index + 1}`, subreddit: "r/ATLHousing" },
  body_preview: "Looking for an apartment near transit. This is synthetic preview data.",
}));
const limitPreview = new URLSearchParams(window.location.search).has("engage-limit");

const SocialsHarness: React.FC = () => new URLSearchParams(window.location.search).has("child") ? <RevisionChildHarness /> : new URLSearchParams(window.location.search).has("copy") ? <CopyHarness /> : new URLSearchParams(window.location.search).has("stage3") ? <Stage3Harness /> : (
  <ThemeProvider theme={theme}>
    <CssBaseline />
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <SocialsPage
        items={REALISTIC_QUEUE}
        totalKnown={29}
        engageLeads={limitPreview ? longEngageQueue : ENGAGE_QUEUE}
        engageCounts={{ needs_reply: limitPreview ? 12 : 3, replied: 0 }}
        onEngageAction={(action) =>
          // eslint-disable-next-line no-console
          console.log("[harness] engage", action)
        }
        onTurnIntoLead={() => undefined}
        // Phase 4 has built no slots, and content_posts really is empty — but
        // `null` here would claim we could not READ it. It has been read: the
        // Directors measured content_posts = 0 on prod today.
        posted={FIXTURE_POSTED}
        scheduled={FIXTURE_SCHEDULED}
        postsThisWeek={counted(1)}
        repliesThisWeek={counted(3)}
        onRefresh={() => undefined}
        unmatched={unknown()}
        // Wired so the picker is clickable in the harness. In the REAL app this
        // stays absent until migration 106 is applied, and every schedule
        // control then says "Scheduling isn't built yet" instead of failing.
        onScheduleSlots={(slots) =>
          // eslint-disable-next-line no-console
          console.log("[harness] would schedule", slots)
        }
        health={HEALTH}
        initialTab={limitPreview ? "engage" : "overview"}
        onSchedule={() => undefined}
        onVerdict={(item, verdict, note) =>
          // eslint-disable-next-line no-console
          console.log("[harness] verdict", { id: item.id, verdict, note })
        }
      />
    </Container>
  </ThemeProvider>
);

export default SocialsHarness;
